import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatTenantDateTime } from "@/lib/datetime";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  Loader2, CloudSun, Snowflake, CloudRain, Sun, Leaf, RefreshCw, Sparkles, Bot, FileEdit, AlertTriangle,
} from "lucide-react";

// Prompt 49 (2026-10-09): blogs por temporada a partir de lo que la gente
// busca en Search Console mes a mes. Las temporadas son las mismas de
// Campañas (invierno = dic-feb, primavera = mar-may, verano = jun-ago,
// otoño = sep-nov).
const SEASON_ICON = { winter: Snowflake, spring: CloudRain, summer: Sun, fall: Leaf };
const RUN_STATUS = {
  generating: { label: "Generando", cls: "bg-[#F59E0B]/10 text-[#F59E0B]" },
  ready: { label: "Lista", cls: "bg-[#10B981]/10 text-[#10B981]" },
  partial: { label: "Parcial", cls: "bg-[#F59E0B]/10 text-[#B45309]" },
  failed: { label: "Falló", cls: "bg-[#EF4444]/10 text-[#EF4444]" },
  stopped_by_cap: { label: "Detenida por tope", cls: "bg-[#EF4444]/10 text-[#EF4444]" },
  interrupted: { label: "Se cortó", cls: "bg-[#EF4444]/10 text-[#EF4444]" },
};
const WRITE_ROLES = ["admin", "manager"];
const MONTH_SHORT = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const LEAD_WEEKS = [2, 3, 4, 5, 6, 8, 10, 12];
const BLOG_COUNTS = [1, 2, 3, 4, 5, 6];

function formatDay(isoDate) {
  if (!isoDate) return "—";
  // Fecha sin hora: se arma al mediodía para que ninguna zona horaria la corra de día.
  return new Date(`${isoDate}T12:00:00`).toLocaleDateString("es-AR", { day: "numeric", month: "long" });
}

function monthsLabel(months) {
  if (!months?.length) return "";
  return `De ${MONTH_SHORT[months[0] - 1]} a ${MONTH_SHORT[months[months.length - 1] - 1]}`;
}

function formatMonth(yyyyMm) {
  if (!yyyyMm) return "";
  return new Date(`${yyyyMm}-15T12:00:00`).toLocaleDateString("es-AR", { month: "short", year: "numeric" });
}

export default function SeasonalPage() {
  const { activeTenantId, activeTenant, role } = useAuth();
  const navigate = useNavigate();
  const canWrite = WRITE_ROLES.includes(role);
  const [season, setSeason] = useState(null);
  const [data, setData] = useState(null);
  const [runs, setRuns] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  const loadOverview = useCallback(async (s) => {
    const res = await api.get("/seasonal/overview", { params: s ? { season: s } : {} });
    setData(res.data);
    setConfig((prev) => prev || res.data.config);
    return res.data;
  }, []);

  const loadRuns = useCallback(async () => {
    const res = await api.get("/seasonal/runs");
    setRuns(res.data);
    return res.data;
  }, []);

  useEffect(() => {
    if (!activeTenantId) return;
    setLoading(true);
    setConfig(null);
    Promise.all([loadOverview(null), loadRuns()])
      .then(([ov]) => setSeason(ov.selected_season.key))
      .catch(() => toast.error("No se pudo cargar Temporadas"))
      .finally(() => setLoading(false));
  }, [activeTenantId, loadOverview, loadRuns]);

  // Mientras haya una tanda generando, se refresca sola cada 3 segundos.
  useEffect(() => {
    if (!runs.some((r) => r.status === "generating")) return undefined;
    const id = setInterval(async () => {
      const fresh = await loadRuns().catch(() => null);
      if (fresh && !fresh.some((r) => r.status === "generating")) {
        loadOverview(season).catch(() => {});
      }
    }, 3000);
    return () => clearInterval(id);
  }, [runs, loadRuns, loadOverview, season]);

  const pickSeason = async (key) => {
    setSeason(key);
    try {
      await loadOverview(key);
    } catch {
      toast.error("No se pudo cargar esa temporada");
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const res = await api.post("/seasonal/sync");
      const { keywords_updated, keywords_added, first_month, last_month } = res.data;
      toast.success(
        `Search Console leído de ${formatMonth(first_month)} a ${formatMonth(last_month)}: ` +
        `${keywords_updated} búsqueda(s) actualizada(s) y ${keywords_added} nueva(s).`,
      );
      await loadOverview(season);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo leer Search Console");
      loadOverview(season).catch(() => {});
    } finally {
      setSyncing(false);
    }
  };

  const startRun = async () => {
    setStarting(true);
    try {
      await api.post("/seasonal/runs", { season, count: config?.blogs_per_season || 3 });
      toast.success("Generando los blogs. Van a quedar en borrador en Contenido.");
      setConfirmOpen(false);
      await loadRuns();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo empezar la tanda");
    } finally {
      setStarting(false);
    }
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      const res = await api.put("/seasonal/config", {
        auto_enabled: !!config.auto_enabled, lead_weeks: config.lead_weeks, blogs_per_season: config.blogs_per_season,
      });
      setConfig(res.data);
      toast.success(res.data.auto_enabled ? "Listo: los blogs de cada temporada se van a generar solos" : "Configuración guardada");
      loadOverview(season).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSavingConfig(false);
    }
  };

  if (loading || !data) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#0055FF]" /></div>;
  }

  const current = data.current_season;
  const next = data.next_season;
  const selected = data.selected_season;
  const selectedLabel = selected.label.toLowerCase();
  const isGenerating = runs.some((r) => r.status === "generating");
  const suspended = activeTenant?.status === "suspended";
  const blogCount = config?.blogs_per_season || 3;
  const available = data.keywords.filter((k) => !k.covered);
  const previewTerms = available.map((k) => k.term).slice(0, blogCount);

  return (
    <div className="space-y-6" data-testid="seasonal-page">
      <p className="text-sm text-[#71717A] max-w-2xl">
        Arma los blogs de cada temporada con lo que la gente busca en Google: frío y nieve en invierno, lluvias y
        humedad en primavera, calor en verano. Google tarda semanas en mostrar un blog nuevo, por eso conviene
        generarlos antes de que empiece cada temporada.
      </p>

      <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6 space-y-4" data-testid="seasonal-now-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <CloudSun className="h-5 w-5 text-[#0055FF] mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm text-[#09090B]">
                <span className="text-[#71717A]">Ahora:</span>{" "}
                <strong data-testid="seasonal-current">{current.label}</strong> · {current.theme}
              </p>
              <p className="text-sm text-[#09090B]">
                <span className="text-[#71717A]">Próxima:</span>{" "}
                <strong data-testid="seasonal-next">{next.label}</strong> · {next.theme} — empieza el{" "}
                {formatDay(next.starts_on)} (faltan {next.days_until} días)
              </p>
              <p className={`text-xs ${next.window_open ? "text-[#10B981]" : "text-[#71717A]"}`} data-testid="seasonal-window">
                {next.window_open
                  ? `Es el momento: los blogs de ${next.label.toLowerCase()} conviene tenerlos listos ya.`
                  : `Los blogs de ${next.label.toLowerCase()} conviene generarlos a partir del ${formatDay(next.publish_from)}.`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {data.google.connected ? (
              <Button
                variant="outline" className="rounded-md" onClick={sync} disabled={syncing || !canWrite}
                data-testid="seasonal-sync-button"
              >
                {syncing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                Actualizar desde Search Console
              </Button>
            ) : (
              <Button variant="outline" className="rounded-md" onClick={() => navigate("/dashboard/connections")} data-testid="seasonal-connect-google-button">
                Conectar Google
              </Button>
            )}
            <Button
              className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md" onClick={() => setConfirmOpen(true)}
              disabled={!canWrite || isGenerating || suspended || (available.length === 0 && data.evergreen_available === 0)}
              title={suspended ? "Cuenta suspendida" : undefined}
              data-testid="seasonal-generate-button"
            >
              {isGenerating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
              Generar {blogCount} blog{blogCount === 1 ? "" : "s"} de {selectedLabel}
            </Button>
          </div>
        </div>
        <div className="text-xs text-[#71717A] border-t border-[#E4E4E7] pt-3" data-testid="seasonal-monthly-status">
          {data.monthly_data.last_sync_at ? (
            <>Datos mes a mes de Search Console: {formatMonth(data.monthly_data.first_month)} a{" "}
              {formatMonth(data.monthly_data.last_month)}, leídos el{" "}
              {formatTenantDateTime(data.monthly_data.last_sync_at, activeTenant?.timezone)}.</>
          ) : (
            <>Todavía sin datos mes a mes: por ahora cada búsqueda se ubica en su temporada por lo que dice
              ({data.keywords_total} búsquedas guardadas).</>
          )}
          {data.monthly_data.last_sync_error && (
            <span className="block mt-1 text-[#EF4444]">Última lectura: {data.monthly_data.last_sync_error}</span>
          )}
        </div>
      </Card>

      <div className="flex gap-2 flex-wrap" data-testid="seasonal-tabs">
        {data.seasons.map((s) => {
          const Icon = SEASON_ICON[s.key];
          const active = s.key === season;
          return (
            <button
              key={s.key} type="button" onClick={() => pickSeason(s.key)} data-testid={`seasonal-tab-${s.key}`}
              className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm transition-colors ${
                active ? "bg-[#0055FF] border-[#0055FF] text-white" : "bg-white border-[#E4E4E7] text-[#09090B] hover:border-[#0055FF]"
              }`}
            >
              <Icon className="h-4 w-4" />
              <span className="font-medium">{s.label}</span>
              <span className={active ? "text-white/80" : "text-[#71717A]"}>· {s.keyword_count}</span>
            </button>
          );
        })}
      </div>

      <Card className="rounded-sm border-[#E4E4E7] shadow-none" data-testid="seasonal-keywords-card">
        <div className="px-6 pt-5 pb-2">
          <h3 className="font-heading font-bold text-[#09090B]">Lo que la gente busca en {selectedLabel}</h3>
          <p className="text-xs text-[#71717A]">
            {monthsLabel(selected.months)}. Ya se generaron {selected.blogs_generated} blog(s) de {selectedLabel} para esta temporada.
          </p>
        </div>
        {data.keywords.length === 0 ? (
          <div className="flex flex-col items-center py-12 text-center px-6" data-testid="seasonal-keywords-empty">
            <AlertTriangle className="h-7 w-7 text-[#A1A1AA]" />
            <p className="mt-3 text-sm text-[#71717A] max-w-md">
              Todavía no hay búsquedas de {selectedLabel}. Actualizá desde Search Console o cargá keywords.
              {data.evergreen_available > 0 &&
                ` Mientras tanto, los blogs se arman con ${data.evergreen_available} búsqueda(s) de todo el año, con el enfoque de ${selectedLabel}.`}
            </p>
          </div>
        ) : (
          <Table data-testid="seasonal-keywords-table">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Búsqueda</TableHead>
                <TableHead className="text-right">Impresiones en {selectedLabel}</TableHead>
                <TableHead className="text-right">Impresiones del año</TableHead>
                <TableHead>Por qué es de {selectedLabel}</TableHead>
                <TableHead>Estado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.keywords.map((k) => (
                <TableRow key={k.id} data-testid={`seasonal-keyword-row-${k.id}`}>
                  <TableCell className="font-medium">{k.term}</TableCell>
                  <TableCell className="text-right tabular-nums">{k.season_impressions || "—"}</TableCell>
                  <TableCell className="text-right tabular-nums text-[#71717A]">{k.year_impressions ?? "—"}</TableCell>
                  <TableCell className="text-xs text-[#71717A]">{k.reasons.join(" · ")}</TableCell>
                  <TableCell>
                    {k.covered
                      ? <Badge className="rounded-sm bg-[#71717A]/10 text-[#71717A] hover:bg-[#71717A]/10 border-0">Ya tiene contenido</Badge>
                      : <Badge className="rounded-sm bg-[#10B981]/10 text-[#10B981] hover:bg-[#10B981]/10 border-0">Disponible</Badge>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {data.keywords.length > 0 && data.evergreen_available > 0 && (
          <p className="px-6 py-3 text-xs text-[#71717A] border-t border-[#E4E4E7]">
            Si faltan búsquedas de {selectedLabel}, se completan con {data.evergreen_available} búsqueda(s) que se
            hacen todo el año, escritas con el enfoque de la temporada.
          </p>
        )}
      </Card>

      <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6 space-y-4" data-testid="seasonal-runs-card">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold text-[#09090B]">Blogs de temporada generados</h3>
          <Button variant="outline" size="sm" className="rounded-md" onClick={() => navigate("/dashboard/content")} data-testid="seasonal-open-content-button">
            <FileEdit className="h-3.5 w-3.5 mr-1" /> Ver en Contenido
          </Button>
        </div>
        {runs.length === 0 ? (
          <p className="text-sm text-[#71717A]" data-testid="seasonal-runs-empty">Todavía no se generó ninguna tanda.</p>
        ) : (
          <div className="space-y-3">
            {runs.map((r) => {
              const st = RUN_STATUS[r.status] || RUN_STATUS.failed;
              const label = data.seasons.find((s) => s.key === r.season)?.label || r.season;
              return (
                <div key={r.id} className="border border-[#E4E4E7] rounded-sm p-3 space-y-2" data-testid={`seasonal-run-${r.id}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-sm">
                      <strong>{label}</strong>{" "}
                      <span className="text-[#71717A]">
                        · {r.trigger === "auto" ? "automática" : "manual"} · {formatTenantDateTime(r.created_at, activeTenant?.timezone)}
                      </span>
                    </div>
                    <Badge className={`rounded-sm border-0 ${st.cls}`}>{st.label}</Badge>
                  </div>
                  {r.status === "generating" && <Progress value={r.progress} />}
                  {r.items?.length > 0 && (
                    <ul className="text-xs text-[#09090B] list-disc pl-5 space-y-0.5">
                      {r.items.map((it) => <li key={it.content_item_id}>{it.title}</li>)}
                    </ul>
                  )}
                  {r.skipped?.length > 0 && (
                    <p className="text-xs text-[#71717A]">
                      Salteadas por parecidas a contenido que ya existe: {r.skipped.map((s) => s.term).join(", ")}
                    </p>
                  )}
                  {r.error_message && <p className="text-xs text-[#EF4444]">{r.error_message}</p>}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {config && (
        <Card className="rounded-sm border-[#E4E4E7] shadow-none p-4" data-testid="seasonal-auto-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Bot className="h-5 w-5 text-[#0055FF]" />
              <div>
                <p className="font-heading font-bold text-sm text-[#09090B]">Generar solos los blogs de cada temporada</p>
                <p className="text-xs text-[#71717A] max-w-md">
                  Una tanda por temporada, unas semanas antes de que empiece. Cada blog usa la IA de la cuenta y queda
                  en borrador en Contenido: nada se aprueba ni se publica solo.
                </p>
                {config.last_auto_result && (
                  <p className="text-xs text-[#71717A] mt-1" data-testid="seasonal-auto-last-result">Última vez: {config.last_auto_result}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Label className="text-xs text-[#71717A]">Semanas antes</Label>
                <Select value={String(config.lead_weeks)} onValueChange={(v) => setConfig({ ...config, lead_weeks: parseInt(v, 10) })} disabled={!canWrite}>
                  <SelectTrigger className="h-8 w-20 text-xs" data-testid="seasonal-lead-weeks-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{LEAD_WEEKS.map((w) => <SelectItem key={w} value={String(w)}>{w}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-[#71717A]">Blogs por temporada</Label>
                <Select value={String(config.blogs_per_season)} onValueChange={(v) => setConfig({ ...config, blogs_per_season: parseInt(v, 10) })} disabled={!canWrite}>
                  <SelectTrigger className="h-8 w-16 text-xs" data-testid="seasonal-blogs-per-season-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{BLOG_COUNTS.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={!!config.auto_enabled} onCheckedChange={(v) => setConfig({ ...config, auto_enabled: v })}
                  disabled={!canWrite} data-testid="seasonal-auto-switch"
                />
                <span className="text-xs text-[#71717A]">{config.auto_enabled ? "Prendido" : "Apagado"}</span>
              </div>
              <Button size="sm" className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md" onClick={saveConfig} disabled={!canWrite || savingConfig} data-testid="seasonal-save-config-button">
                {savingConfig && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />} Guardar
              </Button>
            </div>
          </div>
        </Card>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="seasonal-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Generar {blogCount} blog{blogCount === 1 ? "" : "s"} de {selectedLabel}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-[#71717A]">
                {previewTerms.length > 0 ? (
                  <>
                    <p>Se van a escribir a partir de estas búsquedas{previewTerms.length < blogCount ? ", más búsquedas de todo el año con el enfoque de la temporada" : ""}:</p>
                    <ul className="list-disc pl-5 text-[#09090B]">{previewTerms.map((t) => <li key={t}>{t}</li>)}</ul>
                  </>
                ) : (
                  <p>Se van a escribir con búsquedas de todo el año, con el enfoque de {selectedLabel}.</p>
                )}
                <p>Quedan en borrador en Contenido para revisar. Nada se aprueba ni se publica solo.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="seasonal-confirm-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); startRun(); }} disabled={starting} className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="seasonal-confirm-generate">
              {starting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Generar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
